// Internal layout primitives: Section / FieldRow / FooterActions.
// Shared by SingleForm, BatchForm, AIForm so they all render against
// the same column-flex shell + 75-octet folded footer.

import type { ReactNode } from 'react';
import { Toolbar } from '@viquard/doc-ui/components';
import { useCalendarMessages } from '../../i18n.js';

export function Section(props: {
  title: string;
  children: ReactNode;
}): ReactNode {
  return (
    <section className="vq-cal__composer-section">
      <header className="vq-cal__composer-section-head">
        <span>{props.title}</span>
      </header>
      {props.children}
    </section>
  );
}

export function FieldRow(props: {
  label: string;
  filled?: boolean;
  required?: boolean;
  children: ReactNode;
}): ReactNode {
  return (
    <label className="vq-cal__composer-field">
      <span
        className="vq-cal__composer-field-label"
        data-filled={props.filled === true ? 'true' : undefined}
      >
        <span className="vq-cal__composer-field-text">
          {props.label}
          {props.required === true && (
            <span aria-hidden="true" className="vq-cal__composer-field-required">
              *
            </span>
          )}
        </span>
      </span>
      {props.children}
    </label>
  );
}

/**
 * Footer is a flex-shrink:0 sibling to the scroll region so the action
 * bar never overlaps content and never participates in the scroll.
 *
 * Wrapped in a doc-ui `Toolbar` for keyboard arrow nav across actions.
 */
export function FooterActions(props: {
  primary: ReactNode;
  secondary: ReactNode;
  leading?: ReactNode;
}): ReactNode {
  const messages = useCalendarMessages();
  const { primary, secondary, leading } = props;
  return (
    <div className="vq-cal__composer-footer">
      <Toolbar
        aria-label={messages.composerActionsAriaLabel}
        size="sm"
        className="vq-cal__composer-footer-bar"
      >
        {leading !== null && leading !== undefined ? (
          <span className="vq-cal__composer-footer-leading">{leading}</span>
        ) : null}
        <Toolbar.Spacer />
        {secondary}
        {primary}
      </Toolbar>
    </div>
  );
}
